import {
  FormComboBox,
  FormController,
  FormFileUpload,
  FormInput,
  FormSelect,
  SelectionTable,
} from '@/components/form';
import { ATTACHMENT_MAX_FILE_SIZE_BYTES } from '@/constants/uploadLimits';
import { useDebounce } from '@/hooks';
import { useAppSelector } from '@/state';
import { AuthsApi, ProjectsApi, ReleasesApi, RfiApi } from '@/state/api';
import { RfiSubmittalsType } from '@/types';
import { rfiImpact, rfiPriority, rfiStatus } from '@/types/enums';
import {
  Badge,
  Box,
  Button,
  FileUploadFileMimeType,
  Flex,
  Text,
} from '@chakra-ui/react';
import {
  Avatar,
  Card,
  CheckBoxGroup,
  Input,
  OverlayDialog,
  TableColumnsConfigType,
  toasterApi,
} from '@ui';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { useMemo, useState } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  useWatch,
} from 'react-hook-form';
import { FaRegArrowAltCircleDown, FaRegArrowAltCircleUp } from 'react-icons/fa';
import { FiMinusCircle } from 'react-icons/fi';
import RfiDetails from './RfiDetails';
dayjs.extend(isBetween);

const Rfi = () => {
  const [showSlideForRfiItem, setShowSlideForRfiItem] = useState<{
    rfi_id: number;
  } | null>(null);
  const rfiSubmittalTableColumns = getRfiSubmittalTableColumns(
    setShowSlideForRfiItem,
  );

  const selectedProject = useAppSelector(
    (state) => state.project.selectedProject,
  );
  const selectedRelease = useAppSelector(
    (state) => state.release.selectedRelease,
  );
  const currentUser = useAppSelector((state) => state.auth.current.user);
  const { data: getAllProjects } = ProjectsApi.Api.useGetAllProjectsQuery();

  // POST - create rfi
  const [postCreateRfi] = RfiApi.Api.usePostRfiMutation();

  // POST - upload attachments
  const [postRfiAttachmentS3PreSignMutation] =
    RfiApi.Api.usePostRfiAttachmentS3PreSignMutation(); // get presigned url from S3
  const [putRfiAttachmentUploadToS3Mutation] =
    RfiApi.Api.usePutRfiAttachmentUploadToS3Mutation(); // upload to S3

  // search rfi/ subject/id/spec
  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedSearchedRfis = useDebounce(searchTerm);

  const { data: allRfis } = RfiApi.Api.useGetAllRfisQuery();
  const filteredRfis = useMemo(
    () =>
      allRfis?.filter(
        (rfi) =>
          rfi.project === selectedProject?.id &&
          (rfi.id === Number(debouncedSearchedRfis) ||
            rfi.subject
              .toLowerCase()
              .includes(debouncedSearchedRfis.toLowerCase())),
      ),
    [allRfis, selectedProject, debouncedSearchedRfis],
  );

  const analytics = useMemo(() => {
    const openRfis = filteredRfis?.length || 0;
    const overdueRfis =
      filteredRfis?.filter(
        (rfi) => dayjs(rfi.due_date).toDate() < dayjs().toDate(),
      )?.length || 0;
    const ballInCourt =
      filteredRfis?.filter((rfi) => rfi.ball_in_court === currentUser?.id)
        ?.length || 0;
    const dueThisWeek = filteredRfis?.filter((rfi) =>
      dayjs(rfi.due_date).isBetween(
        dayjs().startOf('week'),
        dayjs().endOf('week'),
      ),
    );
    return {
      openRfis,
      overdueRfis,
      ballInCourt,
      submittalsInReview: 0,
      dueThisWeek,
    };
  }, []);

  if (showSlideForRfiItem) {
    return (
      <Flex flexDirection="column" gapY={4}>
        <RfiDetails
          showSlideForRfiItem={showSlideForRfiItem}
          setShowSlideForRfiItem={setShowSlideForRfiItem}
        />
      </Flex>
    );
  }

  const handleCreateRfi = async (formData: RfiSubmittalsType.Rfi.Post) => {
    let preSignedS3UrlsForAttachments:
      | {
          filename: string;
          upload_url: string;
          file_url: string;
        }[]
      | undefined;

    if (formData?.attachments?.length) {
      try {
        const attachmentsFileNameAndContent =
          formData?.attachments?.map((attachment) => ({
            filename: attachment?.name,
            content_type: attachment?.type,
            size: attachment?.size,
          })) ?? [];

        // Get S3 presigned urls
        const signedS3Urls = await postRfiAttachmentS3PreSignMutation({
          files: attachmentsFileNameAndContent,
        });

        // Upload to S3 using PUT request
        await Promise.all(
          (formData.attachments ?? []).map(async (attachment) => {
            const fileUploadUrl = signedS3Urls?.data?.presigned_urls?.find(
              (url) => url.filename === attachment.name,
            )?.upload_url;

            putRfiAttachmentUploadToS3Mutation({
              content_type: attachment.type,
              file: attachment,
              s3_url: fileUploadUrl!,
            });
          }),
        );

        preSignedS3UrlsForAttachments = signedS3Urls?.data?.presigned_urls;
      } catch {
        toasterApi({
          type: 'error',
          status: 500,
          code: 'Error',
          message: 'Could not upload attachments',
        });
      }
    }

    try {
      await postCreateRfi({
        ...formData,

        // Add attachments using the S3 urls
        ...((formData.attachments?.length &&
          preSignedS3UrlsForAttachments?.length && {
            attachments: preSignedS3UrlsForAttachments?.map((attachment) => ({
              filename: attachment.filename,
              file: attachment.file_url,
            })),
          }) as unknown as File[]),
      });

      toasterApi({
        type: 'success',
        status: 201,
        code: 'Success',
        message: 'RFI created successfully',
      });

      OverlayDialog.close('addRfi');
    } catch {
      toasterApi({
        type: 'error',
        status: 500,
        code: 'Error',
        message: 'Could not create RFI',
      });
    }
  };

  return (
    <Flex flexDirection="column" gapY={4}>
      {/* Badges */}
      <Flex gapX={2}>
        <Badge color="fg" bg="border.emphasized">
          <Text>Open RFIs {analytics?.openRfis}</Text>
        </Badge>
        <Badge>
          <Text>Overdue {analytics?.overdueRfis}</Text>
        </Badge>
        <Badge color="fg" bg="border.emphasized">
          <Text>Ball-in-Court (me) {analytics?.ballInCourt}</Text>
        </Badge>
        <Badge color="fg" bg="border.emphasized">
          <Text>Submittals in Review {analytics?.submittalsInReview}</Text>
        </Badge>
        <Badge color="fg" bg="border.emphasized">
          <Text>Due this week {analytics?.dueThisWeek?.length}</Text>
        </Badge>
      </Flex>

      {/* Search */}
      <Card>
        <Flex flexDirection="column" gapY={4}>
          <Flex gapX={2} alignItems="center">
            <Box
              border="1px solid"
              width="fit-content"
              padding={2}
              borderRadius="md"
            >
              <Text>RFIs</Text>
            </Box>
            <Text>Submittals</Text>
            <Input
              placeholder="Search RFI subject/ ID/ spec"
              onChange={(search) => setSearchTerm(search)}
            />
          </Flex>

          <Text>Overdue this week</Text>
        </Flex>
      </Card>

      {/* Table */}
      <Card>
        <Flex justifyContent="space-between" alignItems="center">
          <Text>RFIs</Text>
          <Button
            onClick={() => {
              OverlayDialog.open('addRfi', {
                overlayId: 'addRfi',
                title: 'Create RFI',
                content: (
                  <Flex>
                    <FormController
                      size="lg"
                      formSchema={RfiSubmittalsType.Rfi.rfiPropsSchema}
                      defaultValues={{
                        subject: '',
                        project: selectedProject?.id as number,
                        release: selectedRelease?.id as number,
                        zone_level: '',
                        status: 'open',
                        priority: 'normal',
                        impact: 'low',
                        open_date: '' as unknown as Date,
                        due_date: '' as unknown as Date,
                        assigned_users: [],
                        discipline: '',
                        ball_in_court: currentUser?.id,
                        attachments: [],
                      }}
                      onFormSubmit={handleCreateRfi}
                    >
                      {({
                        register,
                        control,
                        errors,
                      }: {
                        register: UseFormRegister<RfiSubmittalsType.Rfi.Post>;
                        control: Control<RfiSubmittalsType.Rfi.Post>;
                        errors: FieldErrors<RfiSubmittalsType.Rfi.Post>;
                      }) => {
                        const projectId = useWatch({
                          control,
                          name: 'project',
                        });

                        const { data: getProjectReleases } =
                          ReleasesApi.Api.useGetAllReleaseQuery(undefined, {
                            skip: !projectId,
                          });

                        const {
                          isLoading: isLoadingGetAllUsers,
                          data: getAllUsers,
                        } = AuthsApi.Api.useGetAllUsersQuery();

                        const filteredReleases = useMemo(
                          () =>
                            getProjectReleases?.filter(
                              (release) => release.project === projectId,
                            ),
                          [getProjectReleases, projectId],
                        );

                        // TODO: get roles from user table
                        const roles = [
                          'owner',
                          'coordinator',
                          'reviewer',
                          'watcher',
                        ];

                        return (
                          <Flex flexDirection="column" gapY={6}>
                            <FormInput
                              register={register}
                              control={control}
                              errors={errors}
                              id="subject"
                              label="Subject"
                              type="text"
                              placeholder="Short title or question"
                              showLabelAtTop
                            />
                            <Flex gapX={4}>
                              <FormComboBox
                                register={register}
                                control={control}
                                errors={errors}
                                id="project"
                                label="Project"
                                keyForDisplay="name"
                                keyForValue="id"
                                items={getAllProjects ?? []}
                                showLabelAtTop
                              />
                              <FormComboBox
                                register={register}
                                control={control}
                                errors={errors}
                                id="release"
                                label="Release"
                                keyForDisplay="number"
                                keyForValue="id"
                                items={filteredReleases ?? []}
                                showLabelAtTop
                              />
                            </Flex>
                            <FormInput
                              register={register}
                              control={control}
                              errors={errors}
                              id="zone_level"
                              label="Zone Level"
                              placeholder="e.g. L5 East"
                              showLabelAtTop
                            />
                            <Flex gapX={4}>
                              <FormSelect
                                register={register}
                                control={control}
                                errors={errors}
                                id="status"
                                label="Status"
                                keyForDisplay="label"
                                keyForValue="value"
                                items={rfiStatus}
                                showLabelAtTop
                              />
                              <FormSelect
                                register={register}
                                control={control}
                                errors={errors}
                                id="priority"
                                label="Priority"
                                keyForDisplay="label"
                                keyForValue="value"
                                items={rfiPriority}
                                showLabelAtTop
                              />
                              <FormSelect
                                register={register}
                                control={control}
                                errors={errors}
                                id="impact"
                                label="Impact"
                                keyForDisplay="label"
                                keyForValue="value"
                                items={rfiImpact}
                                showLabelAtTop
                              />
                            </Flex>
                            <Flex gapX={4}>
                              <FormInput
                                register={register}
                                control={control}
                                errors={errors}
                                id="open_date"
                                label="Open Date"
                                showLabelAtTop
                                type="date"
                              />
                              <FormInput
                                register={register}
                                control={control}
                                errors={errors}
                                id="due_date"
                                label="Due Date"
                                showLabelAtTop
                                type="date"
                              />
                            </Flex>
                            <FormInput
                              register={register}
                              control={control}
                              errors={errors}
                              id="discipline"
                              label="Discipline"
                              showLabelAtTop
                            />
                            <Controller
                              control={control}
                              name="assigned_users"
                              render={({ field }) => (
                                <>
                                  <Text
                                    fontSize="sm"
                                    fontWeight="semibold"
                                    color="tertiary.fg"
                                    marginBottom={-4}
                                  >
                                    Assigned users & ball in court
                                  </Text>
                                  <CheckBoxGroup
                                    layout="stack"
                                    disabled={isLoadingGetAllUsers}
                                    items={(getAllUsers ?? []).map((u) => ({
                                      user: u.id,
                                      userName: u.username,
                                    }))}
                                    keyForDisplay="userName"
                                    keyForValue="user"
                                    defaultValue={
                                      field.value?.map((u: any) => u.user) ?? []
                                    }
                                    value={
                                      field.value?.map((u: any) => u.user) ?? []
                                    }
                                    multiple
                                    onSelect={(userIds: number[]) => {
                                      const randomRole =
                                        roles[
                                          Math.floor(
                                            Math.random() * roles.length,
                                          )
                                        ];
                                      const assignedUsers =
                                        userIds.map((id) => ({
                                          user: id,
                                          role: randomRole,
                                        })) ?? [];

                                      field.onChange(assignedUsers);
                                    }}
                                  />
                                  <Text
                                    fontSize="xs"
                                    color="fg.subtle"
                                    marginTop={-4}
                                  >
                                    Select one or more users, give each a role,
                                    and mark at least one as ball in court.
                                  </Text>
                                </>
                              )}
                            />
                            <FormFileUpload
                              control={control}
                              register={register}
                              errors={errors}
                              id="attachments"
                              label="Attachments"
                              maxFiles={10}
                              maxFileSize={ATTACHMENT_MAX_FILE_SIZE_BYTES}
                              accept={
                                'application/*,image/*,text/plain,text/csv' as FileUploadFileMimeType
                              }
                            />
                          </Flex>
                        );
                      }}
                    </FormController>
                  </Flex>
                ),
              });
            }}
          >
            Add RFI
          </Button>
        </Flex>
        <SelectionTable<RfiSubmittalsType.Rfi.Get>
          columns={rfiSubmittalTableColumns}
          items={filteredRfis ?? []}
          stickyColumns
        />
      </Card>
    </Flex>
  );
};
export default Rfi;

const getRfiSubmittalTableColumns = (
  onOpen: (item: { rfi_id: number }) => void,
): TableColumnsConfigType<RfiSubmittalsType.Rfi.Get> => [
  {
    id: {
      label: 'Id',
      width: '100px',
    },
  },
  {
    subject: {
      label: 'Subject',
      width: '100px',
    },
  },
  {
    project: {
      label: 'Project',
      width: '100px',
    },
  },
  {
    release: {
      label: 'Release',
      width: '100px',
    },
  },
  {
    zone_level: {
      label: 'Zone',
      width: '100px',
    },
  },
  {
    ball_in_court: {
      label: 'BIC',
      width: '100px',
      render: (item) => (
        <Flex gapX={2} alignItems="center">
          <Avatar
            name={String(item.ball_in_court)}
            variant="subtle"
            size="xs"
            color="fg"
            bg="border.emphasized"
          />
          <Text>{item.ball_in_court}</Text>
        </Flex>
      ),
    },
  },
  {
    status: {
      label: 'Status',
      width: '100px',
      render: (item) => (
        <Badge colorPalette={'blue'}>
          <Text>{item.status}</Text>
        </Badge>
      ),
    },
  },
  {
    due_date: {
      label: 'Due Date',
      width: '100px',
      render: (item) => (
        <Text>
          {dayjs(item.due_date).format('MM/DD/YYYY')} .{' '}
          {Math.abs(dayjs().diff(dayjs(item.due_date), 'day'))}d
        </Text>
      ),
    },
  },
  {
    priority: {
      label: 'Priority',
      width: '100px',
      render: (item) => (
        <Badge color="fg" bg="border.emphasized">
          <Text>{item.priority}</Text>
        </Badge>
      ),
    },
  },
  {
    impact: {
      label: 'Impact',
      width: '100px',
      render: (item) => (
        <Flex
          gapX={2}
          alignItems="center"
          color={
            item.impact === 'high'
              ? 'fg.error'
              : item.impact === 'medium'
                ? 'fg.warning'
                : 'fg.success'
          }
        >
          {item.impact === 'high' && <FaRegArrowAltCircleUp />}
          {item.impact === 'medium' && <FiMinusCircle />}
          {item.impact === 'low' && <FaRegArrowAltCircleDown />}
          <Text>{item.impact}</Text>
        </Flex>
      ),
    },
  },
  {
    due_date: {
      label: 'Days Open',
      width: '100px',
      render: (item) => {
        const days_open = dayjs().diff(dayjs(item.open_date), 'day');
        const color =
          Number(days_open) > 5
            ? 'fg.error'
            : Number(days_open) > 2
              ? 'fg.warning'
              : 'fg.success';

        return (
          <Badge color={color} bg={color + '/10'}>
            <Text>{days_open}d open</Text>
          </Badge>
        );
      },
    },
  },
  {
    id: {
      label: 'Action',
      width: '100px',
      render: (item) => (
        <Flex>
          <Button size="xs" onClick={() => onOpen({ rfi_id: item.id })}>
            Open
          </Button>
        </Flex>
      ),
    },
  },
];
